import React, { Fragment, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { Box, Button, Menu, MenuItem, Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { ArrowDropDown as ArrowDropDownIcon } from '@material-ui/icons';

import AppSnackbar from '../../../common/AppSnackbar';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { WorkflowDto } from '../Workflow.interface';
import { useWorkflowHasSubTasks } from '../Workflow.hooks';

export interface WorkflowMigrateButtonProps {
  workflow: WorkflowDto;
  onImport?: (config: WorkflowDto['config']) => void;
}

const WorkflowMigrateButton: React.FC<WorkflowMigrateButtonProps> = (
  props: WorkflowMigrateButtonProps
) => {
  const { workflow, onImport } = props;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [action, setAction] = useState<'import' | 'export' | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [importConfig, setImportConfig] = useState<
    WorkflowDto['config'] | undefined
  >();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const { config } = workflow;
  const { statuses, hasSubTasks } = useWorkflowHasSubTasks(
    importConfig || config
  );
  const actionLabel: string = action === 'import' ? 'Import' : 'Export';

  const fileToWorkflow = (file: File) => {
    const reader: FileReader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      const { target } = event;
      if (!target || target.readyState !== 2) {
        return;
      }
      if (target.error) {
        setErrorMessage('Unable to import workflow config.');
        return;
      }

      try {
        const content: string = target.result?.toString() || '';
        const config: WorkflowDto['config'] = JSON.parse(content);
        setImportConfig(config);
      } catch {
        setErrorMessage('Unable to parse the workflow config content.');
      }
    };
    reader.readAsText(file);
  };

  const importWorkflow = () => {
    if (importConfig) {
      onImport?.(importConfig);
      setImportConfig(undefined);
    }
  };

  const exportWorkflow = () => {
    if (config) {
      const blob: Blob = new Blob([JSON.stringify(config, undefined, 2)], {
        type: 'application/json'
      });
      saveAs(blob, `${workflow.name} - Workflow.json`);
    }
  };

  useEffect(() => {
    importConfig && setAction('import');
  }, [importConfig]);

  return (
    <>
      <Button
        color='primary'
        variant='contained'
        endIcon={<ArrowDropDownIcon />}
        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          setMenuAnchorEl(event.currentTarget);
        }}
      >
        Migrate
      </Button>

      <Menu
        id='workflow-migrate-menu'
        keepMounted
        open={!!menuAnchorEl}
        anchorEl={menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem onClick={() => fileInputRef.current?.click()}>
          Import Workflow
        </MenuItem>
        <MenuItem
          disabled={!config}
          onClick={() => {
            hasSubTasks ? setAction('export') : exportWorkflow();
          }}
        >
          Export Workflow
        </MenuItem>
      </Menu>

      <input
        hidden
        type='file'
        accept='application/json'
        ref={fileInputRef}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const { files } = event.target;
          if (files && files.length > 0) {
            fileToWorkflow(files[0]);
          }
          // clear input field
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
      />

      <ConfirmDialog
        open={!!action}
        title={`Confirm ${actionLabel} Workflow`}
        confirmLabel={`Confirm and ${actionLabel}`}
        message={
          <>
            {action === 'import' && (
              <Box mb={hasSubTasks ? 2 : undefined}>
                <Typography variant='body2'>
                  Are you sure you want to import this workflow? This will
                  overwrite the existing workflow.
                </Typography>
              </Box>
            )}
            {hasSubTasks && (
              <Alert severity='warning'>
                <AlertTitle>
                  {`${actionLabel}ing`} Workflow contains SubTasks
                </AlertTitle>
                <Box>
                  This workflow contains SubTask information that might need to
                  be updated when being imported to other environments.
                </Box>
                <Box mt={1}>
                  States that contain SubTasks:{' '}
                  {statuses.map((status: string, index: number) => (
                    <Fragment key={`${status}-${index}`}>
                      <strong>{status}</strong>
                      {index < statuses.length - 1 ? ', ' : ''}
                    </Fragment>
                  ))}
                </Box>
              </Alert>
            )}
          </>
        }
        onCancel={() => {
          setAction(undefined);
          action === 'import' && setImportConfig(undefined);
        }}
        onConfirm={() => {
          setAction(undefined);
          action === 'import' ? importWorkflow() : exportWorkflow();
        }}
      />

      <AppSnackbar
        severity='error'
        open={!!errorMessage}
        onClose={() => setErrorMessage(undefined)}
      >
        {errorMessage}
      </AppSnackbar>
    </>
  );
};

export default WorkflowMigrateButton;
